import { InfoOutlined } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { comparer, computed, IComputedValue, observable, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import ControleGabineteStore from '../../store/ControleGabineteStore';
import GabineteStore from '../../store/GabineteStore';
import ProcessoStore from '../../store/ProcessoStore';
import VisualizaCountProcesso from './VisualizaCountProcesso';
import { TipoControle } from '../../modelo';

interface IPropsLista {
    isConfig?: boolean;
    servidoresMock?: any[];
    controleNumero?: number;
    controleTitulo?: string;
    apenasProcessos?: boolean;
    onDetectarQuantidadeServidores?: (possuiMaisDeUm: boolean) => void;
}

interface tipoServidor { cpf: string, nome: string, count: number }
interface IContagemResponsavel { cpf: string | null, count: number }

// Contagem de processos por responsável de um controle. É um computed com igualdade estrutural: se as
// contagens não mudaram, quem observa não é notificado, mesmo que o índice de processos tenha sido
// recalculado por causa de outros controles.
//
// Fica fora do componente de propósito: o mobx-react não permite ler `this.props` dentro de um
// computed/derivação (só no render), então os parâmetros entram por argumento e o computed é
// guardado por (controle, filtro de OJC).
const calcularContagemPorResponsavel = (
    controleNumero: number,
    ojc: string | string[] | null
): IContagemResponsavel[] => {
    const controle = ControleGabineteStore.listaDeControles.find(c => c.numero === controleNumero);
    const filtrarPorFluxo = !!controle && (
        controle.isTipo(TipoControle.LISTAGEM) ||
        controle.isTipo(TipoControle.DISTRIBUIR)
    );

    const processos = ProcessoStore.processosDoControle(controleNumero, filtrarPorFluxo, ojc);

    const contagens = new Map<string | null, IContagemResponsavel>();

    processos?.forEach((proc) => {
        const cpfNormalizado = proc.numeroCpfResponsavel?.toString().trim() || null;

        const contagem = contagens.get(cpfNormalizado);
        if (contagem) {
            contagem.count++;
        } else {
            contagens.set(cpfNormalizado, { cpf: cpfNormalizado, count: 1 });
        }
    });

    return Array.from(contagens.values());
};

const contagensCache = new Map<string, IComputedValue<IContagemResponsavel[]>>();

const contagemPorResponsavel = (controleNumero: number, ojc: string | string[] | null): IContagemResponsavel[] => {
    const chave = `${controleNumero}|${JSON.stringify(ojc ?? null)}`;

    let contagem = contagensCache.get(chave);
    if (!contagem) {
        contagem = computed(() => calcularContagemPorResponsavel(controleNumero, ojc), { equals: comparer.structural });
        contagensCache.set(chave, contagem);
    }

    return contagem.get();
};

// Ordenação determinística (nome e, em empate, cpf): sem isso a ordem dos botões podia variar
// entre renders e o botão sob o cursor passava a mostrar outro responsável.
const compararServidores = (s1: tipoServidor, s2: tipoServidor) => {
    if (s1.nome !== s2.nome) {
        return s1.nome > s2.nome ? 1 : -1;
    }
    if (s1.cpf === s2.cpf) {
        return 0;
    }
    return (s1.cpf ?? '') > (s2.cpf ?? '') ? 1 : -1;
};


class ListaCountProcessosPorServidor extends React.Component<IPropsLista> {
    carregando = true;
    private ultimoTotalServidores?: number;

    recuperarNomeServidor = (servidores) => {
        return servidores.map(servidor => {
            if (!servidor.cpf || !servidor.cpf.toString().trim()) {
                servidor.nome = "Sem Responsável";
                return servidor;
            }

            if (GabineteStore.deveAguardarNomeServidor(servidor.cpf)) {
                servidor.nome = servidor.cpf.toString().trim().padStart(11, '0');
                return servidor;
            }

            const nome = GabineteStore.recuperarNomeServidor(servidor.cpf);
            servidor.nome = nome || servidor.cpf;
            return servidor;
        });
    }

    constructor(props: IPropsLista) {
        super(props);

        makeObservable(this, {
            carregando: observable,
        });
    }

    get servidores(): tipoServidor[] {
        if (this.props.isConfig) {
            return [];
        }

        const contagens = contagemPorResponsavel(this.props.controleNumero ?? -1, ControleGabineteStore.filtroOJC);
        const servidores = contagens.map(contagem => ({ ...contagem, nome: '' }));

        return this.recuperarNomeServidor(servidores).sort(compararServidores);
    }

    notificarQuantidade() {
        const total = this.servidores?.length ?? 0;

        if (this.ultimoTotalServidores === total) {
            return;
        }

        this.ultimoTotalServidores = total;

        this.props.onDetectarQuantidadeServidores?.(total > 1);
    }

    atualizarServidoresNaStore() {
        const nomes = this.servidores?.map(s => s.nome) ?? [];

        ControleGabineteStore.setServidoresDoControle(
            this.props.controleNumero!,
            nomes
        );
    }

    // Precisa ser método de protótipo, não class field de arrow function
    // (`componentDidMount = () => {}`): em React 19 + mobx-react 9, um campo de
    // instância nessa posição sobrescreve o hook que o observer() injeta no
    // protótipo para acompanhar re-renders, quebrando a reatividade do
    // componente permanentemente após a primeira montagem.
    componentDidMount() {
        if (this.props.isConfig) {
            return;
        }

        const controle = ControleGabineteStore.listaDeControles.find(controle => this.props.controleNumero == controle.numero);
        if (!controle) {
            return;
        }

        this.notificarQuantidade();
        this.atualizarServidoresNaStore();
    }

    componentDidUpdate() {
        if (this.props.isConfig) {
            return;
        }

        this.atualizarServidoresNaStore();
        this.notificarQuantidade();
    }

    render() {
        const servidores = this.servidores;

        return (
            <div id="quantidadeMinutas">
                {this.props.isConfig ?
                    (this.props.servidoresMock.map((servidor, index) => {
                        return (
                            <VisualizaCountProcesso
                                key={index}
                                controleTitulo={this.props.controleTitulo}
                                servidor={servidor}
                                controleNumero={this.props.controleNumero}
                                isConfig={this.props.isConfig}
                            />
                        );
                    }))
                    :
                    servidores.length > 0 ?
                        (servidores.map((servidor) => {
                            return (
                                <VisualizaCountProcesso
                                    key={servidor.cpf ?? 'sem-responsavel'}
                                    servidor={servidor}
                                    controleNumero={this.props.controleNumero}
                                    controleTitulo={this.props.controleTitulo}
                                />
                            );
                        }))
                        :
                        servidores ?
                            <div className="empty-state">
                                <InfoOutlined fontSize="small" style={{ marginRight: 6 }} />
                                <span>
                                    Não há processos ou minutas associadas a responsáveis neste controle.
                                </span>
                            </div>
                            :
                            <CircularProgress size={40} thickness={2} />
                }
            </div>
        );
    }
}

export default observer(ListaCountProcessosPorServidor);
